import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import classNames from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import styles from "./Tabs.module.css";

type BaseUIClassName<State> =
  | string
  | ((state: State) => string | undefined)
  | undefined;

function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
) {
  return (state: State) =>
    classNames(
      houseClass,
      typeof className === "function" ? className(state) : className,
    );
}

export type TabsProps = BaseTabs.Root.Props;

/**
 * A row of tabs over a rule, the active one underlined. Holds the active
 * value; pair it with a {@link TabsList} of {@link Tab}s and, for in-page
 * content, a {@link TabsPanel} per tab.
 *
 * A tab that is a link (each tab its own URL) renders through `render` and
 * takes its value from the route rather than from clicks.
 *
 * @example
 * ```tsx
 * <Tabs value={activeTab}>
 *   <TabsList aria-label="Settings">
 *     <Tab value="tokens" render={<Link to="/settings/tokens" />} nativeButton={false}>
 *       Tokens
 *     </Tab>
 *   </TabsList>
 * </Tabs>
 * ```
 */
export function Tabs({ className, ...props }: TabsProps) {
  return (
    <BaseTabs.Root
      className={withHouseClass(styles.root, className)}
      {...props}
    />
  );
}

export type TabsListProps = Omit<BaseTabs.List.Props, "className"> & {
  /** Applied to the strip: the list, its scroll buttons and the rule. */
  className?: string;
};

/**
 * The row of {@link Tab}s, with the underline that follows the active one.
 * When the tabs overflow, arrow buttons scroll toward the hidden ones, and
 * the active tab is kept in view.
 */
export function TabsList({ className, children, ...props }: TabsListProps) {
  const [list, setList] = React.useState<HTMLDivElement | null>(null);
  const overflow = useOverflow(list);

  const scroll = (direction: -1 | 1) =>
    list?.scrollBy({ left: direction * list.clientWidth * 0.8 });

  return (
    <div className={classNames(styles.strip, className)}>
      {overflow.any && (
        <ScrollButton
          label="Scroll tabs left"
          hidden={!overflow.start}
          onClick={() => scroll(-1)}
        >
          <ChevronLeft aria-hidden />
        </ScrollButton>
      )}
      <BaseTabs.List ref={setList} className={styles.list} {...props}>
        {children}
        <BaseTabs.Indicator className={styles.indicator} />
      </BaseTabs.List>
      {overflow.any && (
        <ScrollButton
          label="Scroll tabs right"
          hidden={!overflow.end}
          onClick={() => scroll(1)}
        >
          <ChevronRight aria-hidden />
        </ScrollButton>
      )}
    </div>
  );
}

/**
 * A pointer affordance only: keyboard users reach every tab with the arrow
 * keys, and focusing a tab scrolls it into view.
 */
function ScrollButton({
  label,
  hidden,
  onClick,
  children,
}: {
  label: string;
  hidden: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      // Hidden rather than removed, so the list keeps its width as it scrolls.
      className={classNames(styles.scrollButton, {
        [styles.isHidden]: hidden,
      })}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Which ends of `list` have tabs scrolled out of view. */
function useOverflow(list: HTMLElement | null) {
  const [overflow, setOverflow] = React.useState({ start: false, end: false });
  const any = overflow.start || overflow.end;

  React.useLayoutEffect(() => {
    if (!list) {
      return undefined;
    }
    const update = () => {
      const start = list.scrollLeft > 1;
      const end = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;
      setOverflow((prev) =>
        prev.start === start && prev.end === end ? prev : { start, end },
      );
    };

    revealActiveTab(list);
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(list);
    const mutationObserver = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName === "data-active")) {
        revealActiveTab(list);
      }
      update();
    });
    mutationObserver.observe(list, {
      childList: true,
      subtree: true,
      attributeFilter: ["data-active"],
    });
    list.addEventListener("scroll", update, { passive: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      list.removeEventListener("scroll", update);
    };
  }, [list]);

  // The scroll buttons narrow the list when they appear.
  React.useLayoutEffect(() => {
    if (list && any) {
      revealActiveTab(list);
    }
  }, [list, any]);

  return { ...overflow, any };
}

/** Scrolls `list` just far enough to show its active tab. */
function revealActiveTab(list: HTMLElement) {
  const tab = list.querySelector<HTMLElement>('[role="tab"][data-active]');
  if (!tab) {
    return;
  }
  const left = tab.offsetLeft;
  const right = left + tab.offsetWidth;
  if (left < list.scrollLeft) {
    list.scrollTo({ left, behavior: "instant" });
  } else if (right > list.scrollLeft + list.clientWidth) {
    list.scrollTo({ left: right - list.clientWidth, behavior: "instant" });
  }
}

export type TabProps = BaseTabs.Tab.Props;

/**
 * One tab in a {@link TabsList}. Icons and badges go in as children beside
 * the label. A disabled tab stays focusable, so a tooltip can say why.
 */
export const Tab = React.forwardRef<HTMLElement, TabProps>(function Tab(
  { className, ...props },
  ref,
) {
  return (
    <BaseTabs.Tab
      ref={ref}
      className={withHouseClass(styles.tab, className)}
      {...props}
    />
  );
});

export type TabsPanelProps = BaseTabs.Panel.Props;

/** The content shown while the {@link Tab} with the same `value` is active. */
export function TabsPanel({ className, ...props }: TabsPanelProps) {
  return (
    <BaseTabs.Panel
      className={withHouseClass(styles.panel, className)}
      {...props}
    />
  );
}

/** A vertical rule between groups of tabs in a {@link TabsList}. */
export function TabsSeparator() {
  return <span aria-hidden className={styles.separator} />;
}
